// ── Capstone surfaces (Wave 8 — 🔒 dark agent lane + pending_schema) ────────
// Wires CapstonePage as the body for both /capstone and /capstone/submissions
// via `customBody` so the route registration + provenance metadata is preserved.
// The surface is honest about every backend reality (no read route → list
// not-configured; dark agent-eval lane → conversion/evaluation unavailable;
// pending_schema → maturity-gated; certificate trigger → not-configured;
// participant DENIED).

import type { ProductScreenContract } from "./product-route-contracts";
import { CapstonePage } from "./capstone/capstone-page";

const CAPSTONE_OWNER = "VIL-20260608-0025";

function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused"
  };
}

function capstoneContract(localComposition: string): ProductScreenContract {
  return {
    title: () => "Capstone",
    primaryJob:
      "Administer capstone submissions: the upload → conversion → evaluation → certificate-trigger pipeline statuses, honest about the dark agent-eval lane.",
    secondaryJob:
      "The submissions list activates with a read route; the structured result schema is maturity-gated (pending_schema); participants are denied the admin module.",
    workTitle: "Capstone administration",
    rows: [
      { id: "evaluation", stage: "Evaluation", state: "unavailable" },
      { id: "certificate-trigger", stage: "Certificate trigger", state: "not-configured" }
    ],
    columns: [
      { key: "stage", header: "Stage" },
      { key: "state", header: "State" }
    ],
    states: ["loading", "permission-denied", "not-configured", "unavailable", "gated", "maturity-gated", "error"],
    apiFamilies: ["session-service"],
    roleDeltas:
      "STAFF perform intake (202, agent lane dark); coach/company_admin/cohort_lead read-only observe; PARTICIPANT DENIED. No participant submit route exists (backend gap).",
    extensionSlots: ["capstone submissions read route", "participant submit route", "evaluation result schema", "certificate trigger"],
    mobilePosture: "Submissions notice, pipeline table, and schema panel stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove the not-configured submissions list, the unavailable agent-eval lane, the pending_schema maturity-gate, and participant denial.",
    vjalComponents: ["Alert", "Badge", "Button", "CollectionPagination", "CollectionToolbar", "DataTableV2", "Module", "ModuleBody", "ModuleHead", "StatusDot"],
    localCompositions: [localComposition],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "The submissions notice (not-configured) + the processing pipeline table.",
      primaryAction: "Review each pipeline stage's honest lane state; staff record an intake from a cohort.",
      secondaryPanel: "The pending_schema maturity-gate; the certificate-trigger not-configured stage.",
      deferredSurface: "Live submissions, evaluation results, and certificate issuance land with their backend lanes.",
      gatedControls: "Participant denied; intake STAFF-only; agent-eval dark; schema pending; certificates not-configured."
    },
    ...focusedDefaults(),
    closureOwner: CAPSTONE_OWNER,
    customBody: CapstonePage
  } as ProductScreenContract;
}

export function capstoneScreen(): ProductScreenContract {
  return capstoneContract("capstone focused product surface");
}

export function capstoneSubmissionsScreen(): ProductScreenContract {
  return capstoneContract("capstone-submissions focused product surface");
}
